import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ListChecks, Sparkles, X } from "lucide-react";
import { IconButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import SetupSection from "./SetupSection";
import SignalRow from "./SignalRow";

/**
 * ActionCenterDrawer — the ranked list, in a right-anchored slide-over.
 *
 * ⚠️ **Not `ui/FilterPanel`, and not a second general drawer in `ui/`.**
 * FilterPanel is a filter panel however it is described: it hardcodes a funnel
 * glyph in its header and an Apply/Reset pair in its footer, and this surface
 * has neither an "apply" nor a "reset". Bending it would mean props that exist
 * only to switch off the thing that makes it a filter panel. Adding a second
 * generic drawer beside it in `ui/` would be worse — that is the two-components-
 * one-name trap the library keeps paying for. So this drawer belongs to this
 * feature and is not exported from it.
 *
 * ── WHAT THE DRAWER CHANGES ABOUT THE CONTENT ───────────────────────────────
 * The dashboard-panel version showed three rows and hid the rest behind
 * "Show 4 more", because vertical space on a dashboard is expensive. A drawer
 * scrolls, so that cost is gone — and with it the reason to truncate. Two
 * things follow:
 *
 *  1. **Everything is shown.** No collapse, no counter, no second click to
 *     reach the fourth most important thing about your institute.
 *  2. **It is GROUPED, not one ranked list.** With three items a single ranked
 *     list is legible; with twelve it is a wall. Setup / work / ideas are three
 *     different kinds of ask, and the reader is deciding which kind they have
 *     time for before they decide which row to click. Ranking still orders
 *     rows inside each group.
 *
 * ── AND THE ONE RULE THAT DID NOT CHANGE ────────────────────────────────────
 * Ideas rank last, always. An admin with twelve overdue follow-ups is never
 * pitched a webinar above them.
 */

const TITLE_ID = "action-center-title";

const SectionHeading = ({ id, children, tone = "muted" }) => (
  <h3
    id={id}
    className={`tw-m-0 tw-bg-surface-alt tw-px-4 tw-py-2 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps ${
      tone === "brand" ? "tw-text-primary" : "tw-text-content-subtle"
    }`}
  >
    {children}
  </h3>
);

const ActionCenterDrawer = ({ open, onClose, state }) => {
  const { loading, setup, attention, discover, dismiss } = state;

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, handleClose]);

  if (!open) return null;

  const nothingLeft = !loading && !setup && !attention.length && !discover.length;

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-z-drawer tw-flex tw-justify-end tw-bg-overlay tw-backdrop-blur-[2px] tw-animate-panel-fade"
      onClick={handleClose}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onClick={(e) => e.stopPropagation()}
        /* ⚠️ `tw-border-solid` alongside `tw-border-l`. Preflight is off, so a
           bare width-and-colour sits on a border whose style is still `none`
           and paints nothing — the same note FilterPanel carries. */
        className="tw-flex tw-h-full tw-w-[26rem] tw-max-w-full tw-flex-col tw-border-0 tw-border-l tw-border-solid tw-border-line tw-bg-surface tw-shadow-popover tw-animate-panel-slide-in motion-reduce:tw-animate-none"
      >
        <header className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-4 tw-py-3.5">
          <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-bg-primary/10 tw-text-primary">
            <ListChecks size={17} aria-hidden="true" />
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <h2
              id={TITLE_ID}
              className="tw-m-0 tw-text-sm tw-font-semibold tw-tracking-tight tw-text-content"
            >
              Things to do
            </h2>
            <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-content-muted">
              For this institute, updated as you work
            </p>
          </div>
          <IconButton label="Close things to do" icon={X} size="sm" onClick={handleClose} />
        </header>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          {loading && (
            <div className="tw-p-4">
              <Shimmer height={56} count={3} />
            </div>
          )}

          {nothingLeft && (
            /* The one place an "all clear" is honest: the reader ASKED. On the
               dashboard the same message was furniture nobody had requested,
               which is why the panel there rendered nothing instead. */
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-6 tw-py-16 tw-text-center">
              <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-md tw-bg-success/10 tw-text-success">
                <Sparkles size={20} aria-hidden="true" />
              </span>
              <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-content">Nothing needs you</p>
              <p className="tw-mb-0 tw-text-xs tw-leading-relaxed tw-text-content-muted">
                Your institute is set up, and there is no work sitting unattended.
              </p>
            </div>
          )}

          {!loading && setup && (
            <SetupSection setup={setup} onHide={() => dismiss("setup.guide")} />
          )}

          {!loading && attention.length > 0 && (
            <section aria-labelledby="ac-attention-heading">
              <SectionHeading id="ac-attention-heading">
                Needs your attention · {attention.length}
              </SectionHeading>
              <ul className="tw-m-0 tw-list-none tw-divide-y tw-divide-solid tw-divide-line-soft tw-p-0">
                {attention.map((signal) => (
                  <SignalRow
                    key={signal.id}
                    signal={signal}
                    /* Work SNOOZES for a week; a feature pitch goes for good.
                       "Not this week" is what an admin means about twelve
                       unassigned leads, and "we are never buying that" is what
                       they mean about an idea. One permanent ✕ would make the
                       first a lie and the second a nag. */
                    onDismiss={(s) => dismiss(s.id, 7)}
                  />
                ))}
              </ul>
            </section>
          )}

          {!loading && discover.length > 0 && (
            <section aria-labelledby="ac-discover-heading">
              <SectionHeading id="ac-discover-heading" tone="brand">
                Already in your plan
              </SectionHeading>
              <ul className="tw-m-0 tw-list-none tw-divide-y tw-divide-solid tw-divide-line-soft tw-p-0">
                {discover.map((signal) => (
                  <SignalRow key={signal.id} signal={signal} onDismiss={(s) => dismiss(s.id, 0)} />
                ))}
              </ul>
            </section>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default ActionCenterDrawer;
